import { Content } from '@radix-ui/react-tabs';
import type { MutableRefObject } from 'react';
import { useEffect, useRef } from 'react';
import { useDraggable } from 'react-use-draggable-scroll';
import { Column } from '../Column';
import { ScrollContainer, TabListWrapper, TabRoot, TabTrigger } from './Tab.styled';
import type { TabProps } from './Tab.types';

export const Tab = ({
  styleComponents,
  tabItem,
  onChange,
  variant = 'line',
  defaultValue,
  size = 'M',
  space,
  value,
  isGroup = false,
  activeStyle,
  inactiveStyle,
  name = '',
}: TabProps) => {
  const scrollRef = useRef<HTMLDivElement>() as MutableRefObject<HTMLInputElement>;
  const { events } = useDraggable(scrollRef, {
    isMounted: true,
  });

  const handleChangeActiveTab = (value: string) => {
    onChange && onChange(value);
  };

  useEffect(() => {
    if (isGroup) {
      const currentActiveTab = document.getElementById('activeTab');

      if (!scrollRef?.current || !currentActiveTab) return;

      const tabIndex = Number(currentActiveTab.dataset.index);

      let totalWidth = 0;
      document.querySelectorAll('[data-name]').forEach((e, index) => {
        if (index >= tabIndex) return;
        totalWidth += e.clientWidth - 20;
      });

      scrollRef.current.scrollTo({ left: totalWidth, behavior: 'smooth' });
    }
  }, [value]);

  return (
    <TabRoot
      defaultValue={defaultValue}
      value={value}
      {...styleComponents?.root}
      className={`tab-${name}-root`}
    >
      <Column className={`tab-${name}-wrapper`} gap={space}>
        <ScrollContainer {...events} className={`tab-${name}-tab-wrapper`} ref={scrollRef}>
          <TabListWrapper
            className={`tab-${name}-tab`}
            isGroup={isGroup}
            variant={variant}
            {...styleComponents?.wrapper}
          >
            {tabItem.map((item, index) => (
              <TabTrigger
                className={`tab-${name}-tab-trigger`}
                data-index={index}
                data-name='tabItem'
                id={value === item.value ? 'activeTab' : ''}
                isGroup={isGroup}
                key={item.value}
                value={item.value}
                {...styleComponents?.trigger}
                activeStyle={activeStyle}
                gap={space}
                inactiveStyle={inactiveStyle}
                onClick={() => handleChangeActiveTab(item.value)}
                size={size}
                variant={variant}
              >
                {item.label}
              </TabTrigger>
            ))}
          </TabListWrapper>
        </ScrollContainer>
        {tabItem.map((item) => (
          <Content className={`tab-${name}-tab-content`} key={item.value} value={item.value}>
            {item.content}
          </Content>
        ))}
      </Column>
    </TabRoot>
  );
};
